<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Database,
  Languages,
  LogOut,
  Menu,
  PackagePlus,
  Settings2,
  UserRoundCog,
  Warehouse,
  Wrench,
  X,
} from 'lucide-vue-next'
import { useAuthStore } from '../../stores/auth'
import { useUiStore } from '../../stores/ui'
import { useConfigStore } from '../../stores/config'
import type { Language, Role } from '../../types'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ui = useUiStore()
const config = useConfigStore()
const { t, locale } = useI18n()

const items = [
  { to: '/orders', label: 'nav.orderList', icon: ClipboardList },
  { to: '/orders/create', label: 'nav.createOrder', icon: Wrench },
  { to: '/parts-orders', label: 'nav.partsOrders', icon: PackagePlus, roles: ['admin', 'brazil', 'naitteng'] as Role[] },
  { to: '/warehouse', label: 'nav.warehouse', icon: Warehouse, roles: ['admin', 'brazil', 'naitteng'] as Role[] },
  { to: '/settings/account', label: 'nav.account', icon: UserRoundCog, roles: ['admin'] as Role[] },
  { to: '/settings/process', label: 'nav.process', icon: Settings2, roles: ['admin'] as Role[] },
  { to: '/import', label: 'nav.data', icon: Database, roles: ['admin'] as Role[] },
]

const visibleItems = computed(() =>
  items.filter((item) => !item.roles || (auth.role && item.roles.includes(auth.role))),
)
const title = computed(() => t(String(route.meta.title || 'app.name')))
const isFullPage = computed(() => Boolean(route.meta.fullPage))
const initials = computed(() => (auth.user?.displayName || auth.user?.username || '?').slice(0, 1).toUpperCase())

watch(() => route.fullPath, () => {
  ui.sidebarOpen = false
})

function isActive(path: string) {
  if (path === '/orders') return route.path === '/orders' || /^\/orders\/\d+$/.test(route.path)
  return route.path === path
}

async function changeLanguage(event: Event) {
  const language = (event.target as HTMLSelectElement).value as Language
  locale.value = language
  await auth.changeLanguage(language)
}

function logout() {
  auth.clear()
  config.reset()
  router.push('/login')
}

onMounted(() => config.load())
</script>

<template>
  <RouterView v-if="isFullPage" />
  <div v-else class="app-layout" :class="{ 'sidebar-is-collapsed': ui.sidebarCollapsed }">
    <button
      v-if="ui.sidebarOpen"
      class="sidebar-backdrop"
      :aria-label="t('app.close')"
      @click="ui.sidebarOpen = false"
    />
    <aside class="sidebar" :class="{ 'is-open': ui.sidebarOpen }">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true"><Archive :size="22" /></div>
        <div class="brand-copy">
          <strong>{{ t('app.shortName') }}</strong>
          <span>{{ t('app.tagline') }}</span>
        </div>
        <button class="icon-button mobile-only sidebar-close" :aria-label="t('app.close')" @click="ui.sidebarOpen = false">
          <X :size="20" />
        </button>
      </div>

      <nav class="sidebar-nav" :aria-label="t('nav.orders')">
        <RouterLink
          v-for="item in visibleItems"
          :key="item.to"
          :to="item.to"
          class="nav-link"
          :class="{ active: isActive(item.to) }"
          :title="ui.sidebarCollapsed ? t(item.label) : undefined"
        >
          <component :is="item.icon" :size="20" aria-hidden="true" />
          <span>{{ t(item.label) }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <div class="sidebar-user">
          <span class="avatar">{{ initials }}</span>
          <div class="sidebar-user-copy">
            <strong>{{ auth.user?.displayName }}</strong>
            <span>{{ auth.role ? t(`role.${auth.role}`) : '' }}</span>
          </div>
        </div>
        <button class="logout-button" type="button" @click="logout">
          <LogOut :size="18" />
          <span>{{ t('nav.logout') }}</span>
        </button>
      </div>
    </aside>

    <div class="app-main">
      <header class="topbar">
        <div class="topbar-leading">
          <button class="icon-button mobile-only" :aria-label="t('nav.openMenu')" @click="ui.sidebarOpen = true">
            <Menu :size="22" />
          </button>
          <button
            class="icon-button desktop-only"
            :aria-label="t(ui.sidebarCollapsed ? 'nav.expand' : 'nav.collapse')"
            @click="ui.toggleCollapsed"
          >
            <ChevronRight v-if="ui.sidebarCollapsed" :size="20" />
            <ChevronLeft v-else :size="20" />
          </button>
          <h1>{{ title }}</h1>
        </div>
        <div class="topbar-actions">
          <label class="language-control">
            <Languages :size="18" aria-hidden="true" />
            <span class="sr-only">{{ t('account.language') }}</span>
            <select :value="locale" @change="changeLanguage">
              <option value="zh">中文</option>
              <option value="en">English</option>
              <option value="pt">Português</option>
            </select>
          </label>
          <span class="topbar-avatar avatar">{{ initials }}</span>
        </div>
      </header>
      <main class="page-content">
        <RouterView />
      </main>
    </div>
  </div>
</template>
